// Country of origin: the API stores the nomenclature code ("MA", "GU91").
// Older rows and Excel imports may still hold a name ("Maroc"), so every form
// loads through toCountryCode() (the select preselects) and every view reads
// through countryDisplayName() (the full French name, never a raw code).

import { countries } from '@/data/countries';

function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/œ/g, 'oe')
    .replace(/['’\-_.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const ALIASES: Record<string, string> = {
  morocco: 'MA', marocain: 'MA', marocaine: 'MA',
  algeria: 'DZ', tunisia: 'TN', french: 'FR', francais: 'FR', francaise: 'FR',
  spain: 'ES', italy: 'IT', germany: 'DE', belgium: 'BE', switzerland: 'CH',
  moroccan: 'MA', algerian: 'DZ', tunisian: 'TN', senegalese: 'SE108', ivorian: 'CO52',
  cameroonian: 'CA35', malian: 'MA76', mauritanian: 'MA78', guinean: 'GU70',
};

const byKey = new Map<string, string>();
for (const [alias, code] of Object.entries(ALIASES)) byKey.set(alias, code);
for (const c of countries) {
  byKey.set(c.code.toUpperCase(), c.code);
  byKey.set(fold(c.nameFr), c.code);
  if (c.nameEn) byKey.set(fold(c.nameEn), c.code);
  if (c.nationality) byKey.set(fold(c.nationality), c.code);
}
const nameByCode = new Map(countries.map((c) => [c.code, c.nameFr]));

/** Nomenclature code for a code or a name; the raw text when unknown; '' when empty. */
export function toCountryCode(value: string | null | undefined): string {
  if (!value) return '';
  const text = value.trim();
  if (!text) return '';
  return byKey.get(text.toUpperCase()) ?? byKey.get(fold(text)) ?? text;
}

/** French country name for a code or a name; the raw text when unknown; fallback when empty. */
export function countryDisplayName(value: string | null | undefined, fallback = '-'): string {
  if (!value || !value.trim()) return fallback;
  const code = toCountryCode(value);
  return nameByCode.get(code) ?? value;
}
